import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Button, CircularProgress } from '@mui/material';
import { Plus, Save } from 'lucide-react';
import { paymentSettingsService } from 'components/api/paymentSettings';
import {
  CatalogueAsset,
  CatalogueDirection,
  CatalogueNetwork,
  CatalogueUnmapped,
  PaymentSettingCatalogue,
} from 'types/paymentSetting';
import ConfirmDeleteModal from 'components/modals/ConfirmDeleteModal';
import { Alert } from 'components/alert/alert';
import { showLoader, hideLoader } from 'store/uiSlice';
import UploadMethodImageModal from './UploadMethodImageModal';
import MethodsList from './MethodsList';
import NetworksView from './NetworksView';
import {
  makeDirectionApi,
  orderItems,
  setCoinLive,
  setCoinOff,
  setNetworkLive,
  setNetworkOff,
} from './catalogueApi';
import styles from './Payments.module.scss';

export interface CreateModalProps {
  open: boolean;
  siteId: string | number;
  onClose: () => void;
  onSuccess: () => void;
}

interface MethodsCatalogueTableProps {
  siteId: string | number;
  direction: CatalogueDirection;
  CreateModal: React.ComponentType<CreateModalProps>;
}

type View = { kind: 'list' } | { kind: 'networks'; settingId: number; assetId: number };

const saveOrderSx = {
  backgroundColor: '#00b3f5',
  '&:hover': { backgroundColor: '#008bbd' },
  '&.Mui-disabled': {
    backgroundColor: 'rgba(0, 179, 245, 0.3)',
    color: 'rgba(255, 255, 255, 0.5)',
  },
  textTransform: 'none',
  fontWeight: 500,
};

/**
 * Payments → Deposit/Withdraw Methods. Screen 1 lists (provider, coin) rows
 * with a Networks (N) button; Screen 2 is the coin's networks for that
 * provider. Both are drag-orderable; both orders are what players see.
 */
const MethodsCatalogueTable: React.FC<MethodsCatalogueTableProps> = ({
  siteId,
  direction,
  CreateModal,
}) => {
  const dispatch = useDispatch();
  const api = useMemo(() => makeDirectionApi(direction), [direction]);

  const [settings, setSettings] = useState<PaymentSettingCatalogue[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<View>({ kind: 'list' });
  const [statusAlert, setStatusAlert] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  // Drag state: coin order per provider, and network order in the open view.
  const [assetOrder, setAssetOrder] = useState<Record<number, number[]>>({});
  const [dirtySettings, setDirtySettings] = useState<Set<number>>(new Set());
  const [networkOrder, setNetworkOrder] = useState<number[]>([]);
  const [networkOrderDirty, setNetworkOrderDirty] = useState(false);

  const [uploadModal, setUploadModal] = useState<{
    open: boolean;
    assetId: number | null;
    assetName: string;
  }>({
    open: false,
    assetId: null,
    assetName: '',
  });
  const [deleteModal, setDeleteModal] = useState<{
    open: boolean;
    assetId: number | null;
    assetName: string;
  }>({
    open: false,
    assetId: null,
    assetName: '',
  });
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    if (!statusAlert) return;
    const t = setTimeout(() => setStatusAlert(null), 5000);
    return () => clearTimeout(t);
  }, [statusAlert]);

  const fetchSettings = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    try {
      const response = await api.list(siteId);
      const data: PaymentSettingCatalogue[] = response.data?.data?.settings ?? [];
      setSettings(data);
    } catch (err: any) {
      setStatusAlert({
        message: err.response?.data?.message || err.message || 'Failed to load methods',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [siteId, api]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Keep coin order in sync with fresh data, but never drop a group the admin
  // has dragged and not yet saved (its ids are kept in their pending order;
  // coins that appeared/disappeared server-side are appended/dropped).
  useEffect(() => {
    setAssetOrder((prev) => {
      const next: Record<number, number[]> = {};
      settings.forEach((s) => {
        const fresh = s.assets.map((a) => a.id);
        if (dirtySettings.has(s.paymentSettingId)) {
          const kept = (prev[s.paymentSettingId] ?? []).filter((id) => fresh.includes(id));
          next[s.paymentSettingId] = [...kept, ...fresh.filter((id) => !kept.includes(id))];
        } else {
          next[s.paymentSettingId] = fresh;
        }
      });
      return next;
    });
    // `dirtySettings` is read at the moment `settings` changes; re-running on
    // its own changes is unnecessary.
  }, [settings]);

  // Resolve the open networks view against fresh data.
  const openSetting =
    view.kind === 'networks'
      ? settings.find((s) => s.paymentSettingId === view.settingId)
      : undefined;
  const openAsset =
    view.kind === 'networks' ? openSetting?.assets.find((a) => a.id === view.assetId) : undefined;

  useEffect(() => {
    if (view.kind === 'networks' && !openAsset && !loading) setView({ kind: 'list' });
  }, [view, openAsset, loading]);

  useEffect(() => {
    if (openAsset && !networkOrderDirty) setNetworkOrder(openAsset.networks.map((n) => n.id));
  }, [openAsset, networkOrderDirty]);

  /** Global loader → call → refetch → alert. Rethrows so EditableField resets its saving flag. */
  const run = async (text: string, fn: () => Promise<unknown>, successMessage: string) => {
    dispatch(showLoader({ text }));
    try {
      await fn();
      await fetchSettings();
      setStatusAlert({ message: successMessage, type: 'success' });
    } catch (err: any) {
      // A composite action may have partially applied — show the real state.
      await fetchSettings().catch(() => undefined);
      setStatusAlert({
        message: err.response?.data?.message || err.message || 'Request failed',
        type: 'error',
      });
      throw err;
    } finally {
      dispatch(hideLoader());
    }
  };
  const swallow = (p: Promise<unknown>) => p.catch(() => undefined);

  // ── Screen 1 actions ──
  const toggleCoin = (setting: PaymentSettingCatalogue, asset: CatalogueAsset, checked: boolean) =>
    swallow(
      run(
        checked ? 'Enabling coin on this provider...' : 'Disabling coin on this provider...',
        () => (checked ? setCoinLive(asset, api) : setCoinOff(asset, api)),
        checked
          ? `${asset.code} is live on ${setting.paymentName}`
          : `${asset.code} switched off on ${setting.paymentName}`,
      ),
    );
  const renameAsset = (asset: CatalogueAsset, name: string) =>
    run(
      'Saving name...',
      () => paymentSettingsService.updateAsset(asset.id, { name }),
      'Name saved',
    );
  const toggleUnmapped = (row: CatalogueUnmapped, checked: boolean) =>
    swallow(
      run(
        'Updating row...',
        () => api.toggleMethod(row.id, checked),
        `${row.code} ${checked ? 'enabled' : 'disabled'}`,
      ),
    );
  const deleteIcon = async () => {
    const id = deleteModal.assetId;
    setDeleteModal({ ...deleteModal, open: false });
    if (!id) return;
    await swallow(
      run('Deleting icon...', () => paymentSettingsService.deleteAssetImage(id), 'Icon removed'),
    );
  };
  const saveAssetOrder = () =>
    swallow(
      run(
        'Saving order...',
        async () => {
          for (const settingId of Array.from(dirtySettings)) {
            await paymentSettingsService.reorderAssets(orderItems(assetOrder[settingId] ?? []));
          }
          setDirtySettings(new Set());
        },
        'Order saved',
      ),
    );

  // ── Screen 2 actions ──
  const renameNetwork = (n: CatalogueNetwork, name: string) =>
    run('Saving name...', () => paymentSettingsService.updateNetwork(n.id, { name }), 'Name saved');
  const saveMin = (n: CatalogueNetwork, value: string) =>
    run('Saving minimum...', () => api.saveMin(n.method, value), 'Minimum saved');
  const toggleLive = (n: CatalogueNetwork, checked: boolean) =>
    swallow(
      run(
        checked ? 'Enabling network...' : 'Disabling network on this provider...',
        () => (checked ? setNetworkLive(n, api, openAsset) : setNetworkOff(n, api)),
        checked
          ? `${n.name} is live on ${openSetting?.paymentName ?? 'this provider'}`
          : `${n.name} switched off on ${openSetting?.paymentName ?? 'this provider'}`,
      ),
    );
  const saveNetworkOrder = () =>
    swallow(
      run(
        'Saving order...',
        () => paymentSettingsService.reorderNetworks(orderItems(networkOrder)),
        'Order saved',
      ).then(() => setNetworkOrderDirty(false)),
    );

  if (loading && settings.length === 0) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', p: 4, height: 400, alignItems: 'center' }}
      >
        <CircularProgress size={40} sx={{ color: '#00b3f5' }} />
      </Box>
    );
  }

  const inNetworks = view.kind === 'networks' && openSetting && openAsset;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
      {statusAlert && <Alert message={statusAlert.message} type={statusAlert.type} />}

      <Box className={styles.toolbar}>
        {!inNetworks && (
          <Button
            variant="contained"
            startIcon={<Plus size={18} />}
            onClick={() => setCreateOpen(true)}
            sx={{
              backgroundColor: '#2a8039',
              '&:hover': { backgroundColor: '#1e5e2a' },
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            Create method
          </Button>
        )}
        <Button
          variant="contained"
          startIcon={<Save size={18} />}
          disabled={inNetworks ? !networkOrderDirty : dirtySettings.size === 0}
          onClick={inNetworks ? saveNetworkOrder : saveAssetOrder}
          sx={saveOrderSx}
        >
          Save Order
        </Button>
      </Box>

      {!inNetworks && dirtySettings.size > 1 && (
        <span className={styles.muted}>
          Coin order is shared by all providers — the last saved group&apos;s order wins.
        </span>
      )}

      {inNetworks ? (
        <NetworksView
          setting={openSetting}
          asset={openAsset}
          api={api}
          order={networkOrder}
          onReorder={(ids) => {
            setNetworkOrder(ids);
            setNetworkOrderDirty(true);
          }}
          onBack={() => {
            setView({ kind: 'list' });
            setNetworkOrderDirty(false);
          }}
          onRenameNetwork={renameNetwork}
          onSaveMin={saveMin}
          onToggleLive={toggleLive}
        />
      ) : settings.length === 0 ? (
        <Box className={styles.providerSection}>
          <span className={styles.muted}>No payment settings for this site.</span>
        </Box>
      ) : (
        <MethodsList
          settings={settings}
          orderBySetting={assetOrder}
          onReorder={(settingId, ids) => {
            setAssetOrder((prev) => ({ ...prev, [settingId]: ids }));
            setDirtySettings((prev) => new Set(prev).add(settingId));
          }}
          onOpenNetworks={(settingId, assetId) => {
            setNetworkOrderDirty(false);
            setView({ kind: 'networks', settingId, assetId });
          }}
          onToggleCoin={toggleCoin}
          onRenameAsset={renameAsset}
          onUploadIcon={(asset) =>
            setUploadModal({ open: true, assetId: asset.id, assetName: asset.code })
          }
          onDeleteIcon={(asset) =>
            setDeleteModal({ open: true, assetId: asset.id, assetName: asset.code })
          }
          onToggleUnmapped={toggleUnmapped}
        />
      )}

      <UploadMethodImageModal
        open={uploadModal.open}
        onClose={() => setUploadModal({ ...uploadModal, open: false })}
        methodId={uploadModal.assetId}
        methodName={uploadModal.assetName}
        uploadFn={paymentSettingsService.uploadAssetImage}
        onSuccess={(message, type) => {
          if (type === 'error') setStatusAlert({ message, type });
          fetchSettings();
        }}
      />

      <ConfirmDeleteModal
        open={deleteModal.open}
        onClose={() => setDeleteModal({ ...deleteModal, open: false })}
        onConfirm={deleteIcon}
        title={`icon for ${deleteModal.assetName}`}
      />

      <CreateModal
        open={createOpen}
        siteId={siteId}
        onClose={() => setCreateOpen(false)}
        onSuccess={() => {
          setStatusAlert({ message: 'Method created', type: 'success' });
          fetchSettings();
        }}
      />
    </Box>
  );
};

export default MethodsCatalogueTable;
